import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import {
  ArrowLeft,
  User,
  Building,
  Mail,
  Phone,
  Shield,
  CheckCircle,
  AlertCircle,
  Clock,
  GraduationCap,
  Briefcase,
  Download,
  RefreshCw,
  Send,
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import backgroundVerificationService from "../../../api/services/backgroundVerificationService";
import { formatDate } from "../../../utils/stringUtils";
import fileUploadService from "../../../api/services/fileUploadService";
import { Upload, X, Paperclip, FileText, Image as ImageIcon, Eye, Trash2 } from "lucide-react";
import { toast } from "react-hot-toast";
import { API_BASE_URL } from "../../../api/config";

const ViewBackgroundVerification = ({ id: propId }) => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const id = propId || paramId;
  const { user } = useAuth();
  const [verification, setVerification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [error, setError] = useState(null);

  // Verification Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [reason, setReason] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [tempProofUrl, setTempProofUrl] = useState("");
  const [tempPublicId, setTempPublicId] = useState("");
  const [tempResourceType, setTempResourceType] = useState("raw");
  const [tempOriginalName, setTempOriginalName] = useState("");

  // Load background verification data
  useEffect(() => {
    loadBackgroundVerification();
  }, [id]);

  const loadBackgroundVerification = async () => {
    try {
      setIsLoading(true);
      setError(null);



      const result =
        await backgroundVerificationService.getBackgroundVerificationById(id);

      if (result && result.responseCode === 200 && result.data) {


        // Map API response to component state
        const verificationData = {
          id: result.data.id,
          candidateId: result.data.candidateId,
          joiningKitId: result.data.joiningKitId,
          employeeId: result.data.employeeId,
          employeeName: result.data.candidateInfo?.name || "Unknown",
          email: result.data.candidateInfo?.email || "",
          phone: result.data.candidateInfo?.phone || "",
          department: result.data.candidateInfo?.department || "",
          position: result.data.candidateInfo?.position || "",
          verificationType: result.data.verificationType,
          vendor: result.data.vendor || {},
          vendorEmail: result.data.vendor?.email || "",
          priority: result.data.priority,
          status: result.data.status,
          completionPercentage: result.data.completionPercentage,
          initiatedDate: result.data.initiatedDate,
          expectedCompletionDate: result.data.expectedCompletionDate,
          completedDate: result.data.completedDate,
          verificationComponents: result.data.verificationComponents || {},
          vendorReport: result.data.vendorReport,
          hrDecision: result.data.hrDecision,
          verificationHistory: result.data.verificationHistory || [],
          notes: result.data.notes || "",
          cost: result.data.cost || 0,
          result: result.data.result || "",
          address: result.data.candidateInfo?.address || {},
        };


        setVerification(verificationData);
      } else {

        setError("Background verification not found");
        toast.error("Background verification not found");
        navigate("/background_verification");
      }
    } catch (error) {

      setError("Error loading background verification");
      toast.error("Error loading background verification");
      navigate("/background_verification");
    } finally {
      setIsLoading(false);
    }
  };

  // Role-based access control
  const canViewVerification = () => {
    return (
      user?.role === "hr_manager" ||
      user?.role === "super_admin" ||
      user?.role === "hr_executive" ||
      user?.role === "recruiter" ||
      user?.role === "employee"
    );
  };

  const canEditVerification = () => {
    return (
      user?.role === "hr_manager" ||
      user?.role === "super_admin"
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "initiated":
        return "bg-blue-100 text-blue-800";
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "verified":
        return "bg-green-100 text-green-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock className="h-4 w-4" />;
      case "initiated":
        return <Clock className="h-4 w-4" />;
      case "in_progress":
        return <RefreshCw className="h-4 w-4" />;
      case "completed":
        return <CheckCircle className="h-4 w-4" />;
      case "verified":
        return <CheckCircle className="h-4 w-4" />;
      case "failed":
        return <AlertCircle className="h-4 w-4" />;
      case "rejected":
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getProgressColor = (progress) => {
    switch (progress) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "not_required":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const ensureFilenameHasExtension = (filename, mimeType) => {
    const clean = String(filename || 'evidence').trim();
    if (/\.[a-zA-Z0-9]+$/.test(clean)) return clean;
    const type = String(mimeType || '').toLowerCase();
    if (type === 'application/pdf') return `${clean}.pdf`;
    if (type === 'application/msword') return `${clean}.doc`;
    if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return `${clean}.docx`;
    if (type === 'text/plain') return `${clean}.txt`;
    if (type.startsWith('image/jpeg')) return `${clean}.jpg`;
    if (type.startsWith('image/png')) return `${clean}.png`;
    if (type.startsWith('image/gif')) return `${clean}.gif`;
    if (type.startsWith('image/webp')) return `${clean}.webp`;
    return clean;
  };

  const triggerBrowserDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFilenameFromUrl = (url) => {
    try {
      const parsed = new URL(url);
      const segment = decodeURIComponent(parsed.pathname.split('/').pop() || '').trim();
      return /\.[a-zA-Z0-9]+$/.test(segment) ? segment : '';
    } catch {
      return '';
    }
  };

  const extractObjectKeyFromUrl = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      // Remove leading slash from pathname
      let key = urlObj.pathname.startsWith('/') ? urlObj.pathname.slice(1) : urlObj.pathname;
      // Remove bucket name if it's part of the path (virtual-hosted-style)
      if (key.startsWith('mobiloitte-intranet-bucket/')) {
        key = key.replace('mobiloitte-intranet-bucket/', '');
      }
      return key;
    } catch {
      return '';
    }
  };

  const downloadEvidence = async (url, filename) => {
    if (!url) {
      toast.error('Evidence file URL is missing');
      return;
    }

    const resolvedFilename = ensureFilenameHasExtension(
      filename || getFilenameFromUrl(url) || 'evidence',
      ''
    );

    try {
      // Extract object key from URL
      const publicId = extractObjectKeyFromUrl(url);

      if (!publicId) {
        throw new Error('Unable to identify evidence file');
      }

      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${API_BASE_URL}/upload/cloudinary/download?public_id=${encodeURIComponent(publicId)}&filename=${encodeURIComponent(resolvedFilename)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers.get('Content-Disposition');
      let finalFilename = resolvedFilename;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
        if (filenameMatch && filenameMatch[1]) {
          finalFilename = filenameMatch[1];
        }
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      triggerBrowserDownload(blobUrl, finalFilename);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {

      toast.error(`Failed to download ${resolvedFilename}`);
    }
  };

  const handleEdit = () => {
    navigate(`/edit_background_verification/${id}`);
  };

  const handleSendReminder = async () => {
    try {
      setIsSendingReminder(true);


      if (result && result.responseCode === 200) {

        toast.success('Reminder sent successfully');

        // Reload verification data to get updated history
        await loadBackgroundVerification();
      } else {

        toast.error(result?.responseMessage || 'Failed to send reminder');
      }
    } catch (error) {

      toast.error("Failed to send reminder");
    } finally {
      setIsSendingReminder(false);
    }
  };

  const handleRefreshStatus = async () => {
    try {


      const result = await backgroundVerificationService.refreshStatus(id);

      if (result && result.responseCode === 200) {

        toast.success('Status refreshed successfully');

        // Reload verification data to get updated status
        await loadBackgroundVerification();
      } else {

        toast.error(result?.responseMessage || 'Failed to refresh status');
      }
    } catch (error) {

      toast.error("Failed to refresh status");
    }
  };

  const handleDownloadReport = async () => {
    try {
      if (!verification) throw new Error('Verification data not available');

      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const companyName = process.env.REACT_APP_COMPANY_NAME;

      // Header Section
      let yPos = 15;

      // 1. Logo (Top Left)
      try {
        const logoImg = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => reject();
          img.src = '/logo.png';
        });
        doc.addImage(logoImg, 'PNG', 14, 10, 25, 25);
      } catch (e) {

      }

      // 2. Company Info (Top Right)
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(companyName, pageWidth - 14, 18, { align: 'right' });

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      const now = new Date();
      doc.text(`Generated on: ${now.toLocaleDateString()} ${now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}`, pageWidth - 14, 23, { align: 'right' });

      yPos = 45;

      // Title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('BACKGROUND VERIFICATION REPORT', pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Section 1: Candidate Info
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('Candidate Information', 14, yPos);
      yPos += 5;
      doc.setLineWidth(0.1);
      doc.line(14, yPos, pageWidth - 14, yPos);
      yPos += 8;

      doc.setFontSize(10);
      const cInfo = [
        ['Name', (verification.employeeName || 'N/A').toUpperCase()],
        ['ID', verification.employeeId || 'N/A'],
        ['Dept', verification.department || 'N/A'],
        ['Position', verification.position || 'N/A']
      ];

      cInfo.forEach(([label, val]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(`${label}:`, 14, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(String(val), 55, yPos);
        yPos += 6;
      });

      yPos += 6;

      // Section 2: Summary
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('Verification Summary', 14, yPos);
      yPos += 5;
      doc.line(14, yPos, pageWidth - 14, yPos);
      yPos += 8;

      doc.setFontSize(10);
      const summary = [
        ['Assigned Employee', (verification.vendor?.name || 'N/A').toUpperCase()],
        ['Employee ID', verification.vendor?.employeeId || 'N/A'],
        ['Employee Email', verification.vendor?.email || 'N/A'],
        ['Employee Dept', verification.vendor?.department || 'N/A'],
        ['Priority', (verification.priority || 'N/A').toUpperCase()],
        ['Status', (verification.status || 'N/A').replace(/_/g, ' ').toUpperCase()],
        ['Initiated', verification.initiatedDate ? new Date(verification.initiatedDate).toLocaleDateString() : 'N/A'],
        ['Expected', verification.expectedCompletionDate ? new Date(verification.expectedCompletionDate).toLocaleDateString() : 'N/A'],
        ['Result', (verification.result || 'PENDING').toUpperCase()]
      ];

      if (verification.hrDecision && verification.hrDecision.toLowerCase() !== 'pending') {
        summary.push(['HR Decision', verification.hrDecision.toUpperCase()]);
      }

      summary.forEach(([l, v]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(`${l}:`, 14, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(String(v), 55, yPos);
        yPos += 6;
      });
      yPos += 10;

      // Section 3: Components
      if (verification.verificationComponents) {
        if (yPos > 240) { doc.addPage(); yPos = 20; }
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.text('Verification Details', 14, yPos);
        yPos += 5;
        doc.line(14, yPos, pageWidth - 14, yPos);
        yPos += 10;

        Object.entries(verification.verificationComponents).forEach(([comp, dat]) => {
          const name = comp.replace(/([A-Z])/g, ' $1').trim().toUpperCase();
          const stat = (dat?.status || 'PENDING').toUpperCase();

          let reason = dat?.reason;
          if (!reason && dat.auditLogs?.length > 0) {
            const latestLog = dat.auditLogs[dat.auditLogs.length - 1];
            reason = latestLog.reason;
          }
          reason = reason || 'Findings verified successfully.';

          if (yPos > 230) { doc.addPage(); yPos = 20; }

          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.text(name, 14, yPos);
          yPos += 6;

          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          doc.text(`Status: ${stat}`, 20, yPos);
          doc.text(`Updated: ${dat?.verifiedAt ? new Date(dat.verifiedAt).toLocaleDateString() : 'N/A'}`, pageWidth / 2, yPos);
          yPos += 6;

          doc.setFont('helvetica', 'bold');
          doc.text('Findings:', 20, yPos);
          doc.setFont('helvetica', 'normal');
          const splitFindings = doc.splitTextToSize(reason, 135);
          doc.text(splitFindings, 55, yPos);
          yPos += (splitFindings.length * 4.5) + 3;

          if (dat.proofUrl) {
            doc.setFont('helvetica', 'bold');
            doc.text('Evidence:', 20, yPos);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(0, 0, 255);
            const splitUrl = doc.splitTextToSize(dat.proofUrl, 135);
            doc.text(splitUrl, 55, yPos);
            doc.setTextColor(0, 0, 0);
            yPos += (splitUrl.length * 4.5) + 3;
          }

          // History Log (Compact)
          if (dat.auditLogs?.length > 0) {
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(8);
            doc.text('History Trace:', 25, yPos);
            yPos += 4;
            dat.auditLogs.slice().reverse().forEach(log => {
              if (yPos > 265) { doc.addPage(); yPos = 20; }
              const logDate = new Date(log.timestamp).toLocaleDateString();
              const logTxt = `${logDate}: ${log.status.toUpperCase()} by ${log.user || 'HR Officer'} ${log.reason ? `(${log.reason})` : ''}`;
              const splitLog = doc.splitTextToSize(logTxt, 140);
              doc.text(splitLog, 30, yPos);
              yPos += (splitLog.length * 3.5) + 1;

              if (log.proofUrl) {
                doc.setTextColor(0, 0, 255);
                const sUrl = doc.splitTextToSize(`Link: ${log.proofUrl}`, 130);
                doc.text(sUrl, 32, yPos);
                doc.setTextColor(0, 0, 0);
                yPos += (sUrl.length * 3.5) + 1;
              }
            });
          }
          yPos += 10;
        });
      }

      // Notes
      if (verification.notes) {
        if (yPos > 240) { doc.addPage(); yPos = 20; }
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.text('Additional Remarks', 14, yPos);
        yPos += 5;
        doc.line(14, yPos, pageWidth - 14, yPos);
        yPos += 8;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const sNotes = doc.splitTextToSize(verification.notes, 180);
        doc.text(sNotes, 14, yPos);
      }

      // Final Footer
      const totalP = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalP; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(`Page ${i} of ${totalP}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
        doc.text(companyName, 14, doc.internal.pageSize.getHeight() - 10);
      }

      const datePart = now.toISOString().split('T')[0];
      const timePart = now.getHours().toString().padStart(2, '0') + "-" + now.getMinutes().toString().padStart(2, '0');
      doc.save(`BGC_Report_${verification.employeeId}_${datePart}_${timePart}.pdf`);
      toast.success('Report generated successfully');
    } catch (err) {

      toast.error('Failed to generate PDF');
    }
  };

  const handleComponentToggle = (componentName, status) => {
    setSelectedComponent(componentName);
    setNewStatus(status);
    setReason("");
    setSelectedFile(null);
    setTempProofUrl("");
    setTempPublicId("");
    setTempResourceType("raw");
    setTempOriginalName("");
    setIsModalOpen(true);
  };

  const handleFileChangeForModal = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Basic validation
    const maxSize = 25 * 1024 * 1024; // 25MB
    if (file.size > maxSize) {
      toast.error("File size must be less than 25MB");
      return;
    }

    try {
      setSelectedFile(file);
      setIsUploadingFile(true);


      const uploadResult = await fileUploadService.uploadToCloudinary(file);

      if (uploadResult.success) {

        setTempProofUrl(uploadResult.url);
        setTempPublicId(uploadResult.public_id);
        setTempResourceType(uploadResult.resource_type || (file.type.startsWith('image/') ? 'image' : 'raw'));
        setTempOriginalName(file.name);
        toast.success("Document uploaded to secure storage");
      } else {

        toast.error(uploadResult.error || "Failed to upload document");
        setSelectedFile(null); // Reset selection on failure
      }
    } catch (error) {

      toast.error("An unexpected error occurred during upload");
      setSelectedFile(null);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleDeleteEvidence = async () => {
    if (!tempPublicId) {
      setSelectedFile(null);
      setTempProofUrl("");
      return;
    }

    try {
      setIsUploadingFile(true); // Reuse uploading state for deletion progress


      const result = await fileUploadService.deleteFromCloudinary(tempPublicId, tempResourceType);

      if (result.success) {
        toast.success("Evidence deleted successfully");
        setSelectedFile(null);
        setTempProofUrl("");
        setTempPublicId("");
        setTempResourceType("raw");
        setTempOriginalName("");
      } else {
        toast.error(result.error || "Failed to delete from storage");
      }
    } catch (error) {

      toast.error("An unexpected error occurred during deletion");
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleModalSubmit = async () => {
    try {
      if (reason.length > 500) {
        toast.error("Reason cannot exceed 500 characters");
        return;
      }

      setIsSubmitting(true);


      // Use the URL from the direct upload if available, otherwise fall back to existing
      const finalProofUrl = tempProofUrl || verification.verificationComponents[selectedComponent]?.proofUrl || "";
      const finalProofOriginalName = tempOriginalName || verification.verificationComponents[selectedComponent]?.proofOriginalName || "";

      const updateData = {
        verificationComponents: {
          [selectedComponent]: {
            status: newStatus,
            reason: reason,
            proofUrl: finalProofUrl,
            proofOriginalName: finalProofOriginalName,
            verifiedAt: new Date().toISOString()
          }
        }
      };

      const result = await backgroundVerificationService.updateBackgroundVerification(verification.id, updateData);

      if (result && result.responseCode === 200) {

        toast.success(`${selectedComponent.replace(/([A-Z])/g, ' $1').trim().replace(/\b\w/g, l => l.toUpperCase())} ${newStatus === 'completed' ? 'verified' : 'marked as failed'}`);

        await loadBackgroundVerification();
        setIsModalOpen(false);
        setTempProofUrl(""); // Reset for next use
        setTempPublicId("");
        setTempResourceType("raw");
        setTempOriginalName("");
        setSelectedFile(null);
      } else {
        toast.error(result?.responseMessage || 'Failed to update verification status'); // Replaced showError
      }
    } catch (error) {

      toast.error("Failed to update verification status"); // Replaced showError
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!canViewVerification()) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600">
            You don't have permission to view background verifications.
          </p>
        </div>
      </div>
    );
  }

  if (!verification) {
    return (
      <div className="text-center p-8">
        <h2 className="text-2xl font-bold text-red-600">
          Verification Not Found
        </h2>
        <p className="text-gray-600 mt-2">
          The requested background verification could not be found.
        </p>
        <Button
          onClick={() => navigate("/background_verification")}
          className="mt-4"
        >
          Back to Background Verifications
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center space-x-4 min-w-0 flex-1">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-gray-900 break-words">Background Verification Details</h1>
            <p className="text-gray-600 mt-1 break-words">Verification for {verification.employeeName?.replace(/\b\w/g, l => l.toUpperCase())}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3 flex-shrink-0">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
              verification.status
            )}`}
          >
            {getStatusIcon(verification.status)}
            <span className="ml-2 capitalize">
              {verification.status.replace("_", " ")}
            </span>
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Candidate Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Candidate Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3 min-w-0">
                <User className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="font-medium text-gray-900 break-words">{verification.employeeName?.replace(/\b\w/g, l => l.toUpperCase())}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 min-w-0">
                <Building className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600">Candidate ID</p>
                  <p className="font-medium text-gray-900 break-words">
                    {verification.employeeId}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 min-w-0">
                <Mail className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium text-gray-900 break-words break-all">
                    {verification.email}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 min-w-0">
                <Phone className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-medium text-gray-900 break-words">
                    {verification.phone}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 min-w-0">
                <Briefcase className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600">Department</p>
                  <p className="font-medium text-gray-900 break-words">
                    {verification.department}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 min-w-0">
                <GraduationCap className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-600">Position</p>
                  <p className="font-medium text-gray-900 break-words">
                    {verification.position}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Verification Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Verification Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="min-w-0">
                <p className="text-sm text-gray-600">Assigned Employee</p>
                <p className="font-medium text-gray-900 capitalize break-words">
                  {verification.vendor?.name || "N/A"}
                  {verification.vendor?.employeeId && <span className="text-xs text-gray-500 ml-1">({verification.vendor.employeeId})</span>}
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-sm text-gray-600">Employee Email</p>
                <p className="font-medium text-gray-900 break-words break-all">
                  {verification.vendor?.email || verification.vendorEmail || "N/A"}
                </p>
              </div>
              {verification.vendor?.department && (
                <div className="min-w-0">
                  <p className="text-sm text-gray-600">Employee Dept</p>
                  <p className="font-medium text-gray-900 capitalize break-words">
                    {verification.vendor.department}
                  </p>
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm text-gray-600">Priority</p>
                <p className="font-medium text-gray-900 capitalize break-words">
                  {verification.priority}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Initiated Date</p>
                <p className="font-medium text-gray-900">
                  {formatDate(verification.initiatedDate, { includeTime: false })}
                </p>
              </div>
              {verification.completedDate && (
                <div>
                  <p className="text-sm text-gray-600">Completed Date</p>
                  <p className="font-medium text-gray-900">
                    {formatDate(verification.completedDate, { includeTime: false })}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Progress Tracking */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Verification Progress
            </h3>
            <div className="space-y-3">
              {verification.verificationComponents && Object.entries(verification.verificationComponents).map(([check, data]) => (
                <div key={check} className="p-3 bg-gray-50 rounded-lg space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <Shield className="h-5 w-5 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="font-medium text-gray-900 capitalize break-words block">
                          {check.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mt-1 ${getProgressColor(data?.status || 'pending')}`}>
                          {data?.status?.replace('_', ' ') || 'pending'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <button
                        onClick={() => handleComponentToggle(check, 'completed')}
                        className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all transform active:scale-95 ${data?.status === 'completed'
                          ? 'bg-green-600 text-white shadow-md'
                          : 'bg-white border border-green-600 text-green-600 hover:bg-green-50'
                          }`}
                        disabled={isSubmitting}
                      >
                        Verified
                      </button>
                      <button
                        onClick={() => handleComponentToggle(check, 'failed')}
                        className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all transform active:scale-95 ${data?.status === 'failed'
                          ? 'bg-red-600 text-white shadow-md'
                          : 'bg-white border border-red-600 text-red-600 hover:bg-red-50'
                          }`}
                        disabled={isSubmitting}
                      >
                        Unverified
                      </button>
                    </div>
                  </div>

                  {data?.auditLogs && data.auditLogs.length > 0 && (
                    <div className="pl-8 pt-3 border-t border-gray-100 space-y-3">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Verification History</p>
                      <div className="space-y-3 max-h-48 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-200">
                        {[...data.auditLogs].reverse().map((log, index) => (
                          <div key={index} className="bg-white p-2 rounded border border-gray-100 shadow-sm relative overflow-hidden group">
                            <div className={`absolute left-0 top-0 bottom-0 w-1 ${log.status === 'completed' ? 'bg-green-500' : 'bg-red-500'}`} />
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${log.status === 'completed' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                {log.status === 'completed' ? 'VERIFIED' : 'FAILED'}
                              </span>
                              <span className="text-[9px] text-gray-400 font-medium">
                                {formatDate(log.timestamp, { includeTime: true })}
                              </span>
                            </div>
                            {log.reason && (
                              <p className="text-xs text-gray-700 mb-1 leading-relaxed font-medium break-words whitespace-pre-wrap">
                                {log.reason}
                              </p>
                            )}
                            <div className="flex items-center justify-between mt-2">
                              {log.proofUrl ? (
                                <button
                                  type="button"
                                  onClick={() => downloadEvidence(log.proofUrl, log.proofOriginalName || 'evidence')}
                                  className="inline-flex items-center gap-1 text-[10px] font-bold text-agrosaf-green hover:underline"
                                >
                                  <Paperclip className="h-3 w-3" />
                                  View Evidence
                                </button>
                              ) : <span />}
                              <div className="flex flex-col items-end">
                                <span className="text-[9px] text-gray-400 italic">By {log.user || 'N/A'}</span>
                                {log.userEmail && <span className="text-[8px] text-gray-400">{log.userEmail}</span>}
                                {log.userDepartment && <span className="text-[8px] text-gray-400">{log.userDepartment}</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          {
            verification.notes && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Notes
                </h3>
                <p className="text-sm text-gray-700 break-words whitespace-pre-line">
                  {verification.notes}
                </p>
              </div>
            )
          }
        </div>

        {/* Sidebar */}
        <div className="space-y-6" >
          {/* Status Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Status</h3>
            <div className="text-center">
              <div
                className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(
                  verification.status
                )} mb-3`}
              >
                {getStatusIcon(verification.status)}
                <span className="ml-2 capitalize">
                  {verification.status.replace("_", " ")}
                </span>
              </div>

              {verification.result && (
                <div className="mt-3">
                  <p className="text-sm text-gray-600">Result</p>
                  <p
                    className={`font-medium ${verification.result === "passed"
                      ? "text-green-600"
                      : "text-red-600"
                      }`}
                  >
                    {verification.result === "passed" ? "Passed" : "Failed"}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Documents Required */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Documents Required
            </h3>
            <div className="space-y-2">
              {verification.verificationComponents && Object.entries(verification.verificationComponents)
                .map(([doc, data]) => (
                  <div key={doc} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 capitalize">
                      {doc.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <div className="flex items-center space-x-2">
                      {data?.status === 'completed' ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : data?.status === 'failed' ? (
                        <X className="h-4 w-4 text-red-500" />
                      ) : data?.status === 'in_progress' ? (
                        <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-yellow-500" />
                      )}
                      <span className={`text-xs px-2 py-1 rounded-full ${getProgressColor(data?.status || 'pending')}`}>
                        {data?.status === 'completed' ? 'Verified' :
                          data?.status === 'failed' ? 'Failed' :
                            data?.status === 'in_progress' ? 'In Progress' :
                              'Pending'}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Actions
            </h3>
            <div className="space-y-3">
              {(verification.status === "pending" ||
                verification.status === "initiated") &&
                canEditVerification() && (
                  <Button
                    variant="outline"
                    icon={<Send className={`h-4 w-4 ${isSendingReminder ? "animate-spin" : ""}`} />}
                    onClick={handleSendReminder}
                    className="w-full"
                    disabled={isSendingReminder}
                  >
                    {isSendingReminder ? "Sending..." : "Send Reminder"}
                  </Button>
                )}
              <Button
                variant="outline"
                icon={<Download className="h-4 w-4" />}
                onClick={handleDownloadReport}
                className="w-full"
              >
                Download Report
              </Button>
            </div>
          </div>

        </div>
      </div>

      {/* Verification Update Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm !mt-0">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900">
                Update {selectedComponent?.replace(/([A-Z])/g, ' $1').trim().replace(/\b\w/g, l => l.toUpperCase())}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="flex items-center space-x-2 text-sm font-medium">
                <span className="text-gray-500">New Status:</span>
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${newStatus === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                  {newStatus === 'completed' ? 'Verified' : 'Unverified'}
                </span>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-agrosaf-green" />
                  Reason / Outcome (Optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter detailed reason or verification findings..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent transition-all outline-none resize-none h-32 text-sm"
                  maxLength={1500}
                />
                <div className="flex justify-between mt-1.5">
                  <p className="text-[11px] text-gray-400 italic">Provide context for this verification decision.</p>
                  <p className={`text-xs ${reason.length >= 500 ? 'text-red-500 font-bold' : 'text-gray-400'}`}>
                    {reason.length}/1500
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Paperclip className="h-4 w-4 text-agrosaf-green" />
                  Attach Evidence (Optional)
                </label>
                <div className="relative group">
                  <input
                    type="file"
                    onChange={handleFileChangeForModal}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    accept="image/*,.pdf"
                    disabled={isUploadingFile || isSubmitting}
                  />
                  <div className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center transition-all ${isUploadingFile ? 'border-blue-400 bg-blue-50' : selectedFile ? 'border-agrosaf-green bg-green-50' : 'border-gray-200 group-hover:border-agrosaf-green group-hover:bg-gray-50'
                    }`}>
                    {isUploadingFile ? (
                      <>
                        <RefreshCw className="h-8 w-8 text-blue-500 animate-spin mb-2" />
                        <p className="text-sm font-medium text-[#0F2942]">Uploading...</p>
                        <p className="text-xs text-blue-400 mt-1">Please wait while we secure your document</p>
                      </>
                    ) : selectedFile ? (
                      <>
                        {selectedFile.type?.startsWith('image/') ? (
                          <ImageIcon className="h-8 w-8 text-agrosaf-green mb-2" />
                        ) : (
                          <FileText className="h-8 w-8 text-agrosaf-green mb-2" />
                        )}
                        <p className="text-sm font-medium text-gray-800 truncate max-w-[250px]">{selectedFile.name}</p>
                        <p className="text-xs text-gray-500 mt-1">{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</p>

                        <div className="flex items-center gap-2 mt-4 relative z-20">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); downloadEvidence(tempProofUrl, tempOriginalName || selectedFile?.name || 'evidence'); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-md text-xs font-bold text-gray-700 hover:bg-gray-50 transition-all shadow-sm"
                          >
                            <Eye className="h-3.5 w-3.5 text-agrosaf-green" />
                            View
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDeleteEvidence(); }}
                            disabled={isUploadingFile}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-red-100 rounded-md text-xs font-bold text-red-600 hover:bg-red-50 transition-all shadow-sm disabled:opacity-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-gray-300 group-hover:text-agrosaf-green mb-2 transition-colors" />
                        <p className="text-sm font-medium text-gray-600">Click or drag to upload evidence</p>
                        <p className="text-xs text-gray-400 mt-1 whitespace-nowrap">Supports PDF, JPG, PNG (Max 25MB)</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 flex items-center justify-end gap-3 border-t border-gray-100">
              <button
                onClick={() => setIsModalOpen(false)}
                disabled={isSubmitting}
                className="px-5 py-2.5 text-sm font-bold text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleModalSubmit}
                disabled={isSubmitting || isUploadingFile}
                className={`px-6 py-2.5 text-sm font-bold text-white rounded-lg shadow-lg hover:shadow-xl transform active:scale-95 transition-all flex items-center gap-2 ${newStatus === 'completed' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Confirm {newStatus === 'completed' ? 'Verified' : 'Unverified'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ViewBackgroundVerification;
